import { useState, useEffect } from "react";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import NavBar from "@/components/NavBar";
import { Button } from "@/components/ui/button";
import { Plus, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, closestCorners } from "@dnd-kit/core";
import { PipelineStage } from "@/components/pipeline/PipelineStage";
import { DealCard } from "@/components/pipeline/DealCard";
import { DealDialog } from "@/components/pipeline/DealDialog";
import { StageDialog } from "@/components/pipeline/StageDialog";
import { DealDetailView } from "@/components/pipeline/DealDetailView";

interface Stage {
  id: string;
  name: string;
  order_number: number;
  color: string | null;
}

interface Deal {
  id: string;
  title: string;
  value: number | null;
  expected_close_date: string | null;
  pipeline_stage_id: string;
  contact_id: string | null;
  notes: string | null;
  contact?: {
    name: string;
    organization: string | null;
  };
}

const Pipeline = () => {
  const [stages, setStages] = useState<Stage[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeDeal, setActiveDeal] = useState<Deal | null>(null);
  const [isDealDialogOpen, setIsDealDialogOpen] = useState(false);
  const [isStageDialogOpen, setIsStageDialogOpen] = useState(false);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const checkAuthAndInit = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      console.log("Pipeline - Session check:", session ? "Authenticated" : "Not authenticated");
      
      if (!session) {
        console.log("No session found, redirecting to login");
        window.location.href = "/";
        return;
      }
      
      initializePipeline();
    };
    
    checkAuthAndInit();
  }, []);

  const initializePipeline = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.log("User not authenticated, redirecting...");
        window.location.href = "/";
        return;
      }

      // Check if user has stages
      const { data: existingStages } = await supabase
        .from("pipeline_stages")
        .select("*")
        .eq("user_id", user.id)
        .order("order_number");

      if (!existingStages || existingStages.length === 0) {
        // Create default stages
        const defaultStages = [
          { name: "Lemlist", order_number: 1, color: "#FF6B6B" },
          { name: "Contact/Reach Out", order_number: 2, color: "#4ECDC4" },
          { name: "Follow Up 1", order_number: 3, color: "#45B7D1" },
          { name: "Follow Up 2", order_number: 4, color: "#96CEB4" },
          { name: "Send Collateral", order_number: 5, color: "#FFEAA7" },
          { name: "Final Calls", order_number: 6, color: "#DFE6E9" },
          { name: "Demo Scheduled", order_number: 7, color: "#74B9FF" },
          { name: "Reschedule Demo", order_number: 8, color: "#A29BFE" },
          { name: "Demo Completed/Closed", order_number: 9, color: "#00B894" },
        ];

        const stagesToInsert = defaultStages.map(stage => ({
          ...stage,
          user_id: user.id,
        }));

        const { data: newStages, error } = await supabase
          .from("pipeline_stages")
          .insert(stagesToInsert)
          .select();

        if (error) throw error;
        setStages(newStages || []);
      } else {
        setStages(existingStages);
      }

      await fetchDeals();
    } catch (error) {
      console.error("Error initializing pipeline:", error);
      toast({
        title: "Error",
        description: "Failed to initialize pipeline",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDeals = async () => {
    try {
      const { data, error } = await supabase
        .from("deals")
        .select(`
          *,
          contact:contacts(name, organization)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setDeals(data || []);
    } catch (error) {
      console.error("Error fetching deals:", error);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const deal = deals.find(d => d.id === event.active.id);
    setActiveDeal(deal || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDeal(null);

    if (!over || active.id === over.id) return;

    const dealId = active.id as string;
    const newStageId = over.id as string;
    const deal = deals.find(d => d.id === dealId);
    const oldStageId = deal?.pipeline_stage_id;

    // Update local state immediately for smooth UX
    setDeals(deals.map(deal =>
      deal.id === dealId
        ? { ...deal, pipeline_stage_id: newStageId }
        : deal
    ));

    // Update in database and track history
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Update deal stage
      const { error: updateError } = await supabase
        .from("deals")
        .update({ pipeline_stage_id: newStageId })
        .eq("id", dealId);

      if (updateError) throw updateError;

      // Record stage change history
      const { error: historyError } = await supabase
        .from("deal_stage_history")
        .insert([{
          deal_id: dealId,
          user_id: user.id,
          from_stage_id: oldStageId,
          to_stage_id: newStageId,
        }]);

      if (historyError) console.error("History tracking error:", historyError);

      toast({
        title: "Success",
        description: "Deal moved successfully",
      });
    } catch (error) {
      console.error("Error moving deal:", error);
      toast({
        title: "Error",
        description: "Failed to move deal",
        variant: "destructive",
      });
      // Revert on error
      fetchDeals();
    }
  };

  const handleAddDeal = (stageId?: string) => {
    setSelectedDeal(stageId ? { pipeline_stage_id: stageId } as Deal : null);
    setIsDealDialogOpen(true);
  };

  const handleEditDeal = (deal: Deal) => {
    setSelectedDealId(deal.id);
    setIsDetailViewOpen(true);
  };

  const getDealsByStage = (stageId: string) => {
    return deals.filter(deal => deal.pipeline_stage_id === stageId);
  };

  if (loading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <AppSidebar />
          <div className="flex-1 flex flex-col min-w-0">
            <NavBar />
            <main className="flex-1 p-6 overflow-auto pt-20">
              <div className="text-center">Loading pipeline...</div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <NavBar />
          <main className="flex-1 p-6 overflow-auto pt-20">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold">Sales Pipeline</h1>
                  <p className="text-muted-foreground">
                    Drag and drop deals across your sales stages
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsStageDialogOpen(true)}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Manage Stages
                  </Button>
                  <Button onClick={() => handleAddDeal()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Deal
                  </Button>
                </div>
              </div>

              <DndContext
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <div className="flex gap-3 overflow-x-auto pb-4">
                  {stages.map((stage) => (
                    <PipelineStage
                      key={stage.id}
                      stage={stage}
                      deals={getDealsByStage(stage.id)}
                      onAddDeal={() => handleAddDeal(stage.id)}
                      onEditDeal={handleEditDeal}
                    />
                  ))}
                </div>
                <DragOverlay>
                  {activeDeal ? <DealCard deal={activeDeal} isDragging /> : null}
                </DragOverlay>
              </DndContext>
            </div>
          </main>
        </div>
      </div>

      <DealDialog
        open={isDealDialogOpen}
        onOpenChange={setIsDealDialogOpen}
        deal={selectedDeal}
        stages={stages}
        onSuccess={fetchDeals}
      />

      <StageDialog
        open={isStageDialogOpen}
        onOpenChange={setIsStageDialogOpen}
        stages={stages}
        onSuccess={initializePipeline}
      />

      <DealDetailView
        dealId={selectedDealId}
        open={isDetailViewOpen}
        onOpenChange={setIsDetailViewOpen}
        onSuccess={fetchDeals}
      />
    </SidebarProvider>
  );
};

export default Pipeline;
